/**
 * Snackbar queue (material-standard §5): one message at a time, optional Undo, pause while hovered.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export type SnackbarActionKind = 'open' | 'undo';

export interface SnackbarAction {
  /** `undo` is only for client-reversible actions (material-standard §3.1 rule 7). */
  readonly kind: SnackbarActionKind;
  readonly label: string;
  readonly onAction: () => void;
}

export interface SnackbarMessage {
  readonly action?: SnackbarAction;
  readonly description?: string;
  readonly durationMs?: number;
  readonly id?: string;
  readonly title: string;
}

interface QueuedSnackbar extends SnackbarMessage {
  readonly durationMs: number;
  readonly id: string;
}

export interface SnackbarQueueApi {
  readonly current: QueuedSnackbar | null;
  readonly dismiss: () => void;
  readonly enqueue: (message: SnackbarMessage) => void;
  readonly pause: () => void;
  readonly resume: () => void;
}

const DEFAULT_DURATION_MS = 6000;
const UNDO_DURATION_MS = 10_000;

let nextSnackbarId = 1;

function resolveDuration(message: SnackbarMessage): number {
  if (message.durationMs !== undefined) {
    return message.durationMs;
  }
  return message.action === undefined ? DEFAULT_DURATION_MS : UNDO_DURATION_MS;
}

export function useSnackbarQueue(): SnackbarQueueApi {
  const [queue, setQueue] = useState<readonly QueuedSnackbar[]>([]);
  const [paused, setPaused] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const current = queue[0] ?? null;

  const dismiss = useCallback(() => {
    setQueue((items) => items.slice(1));
  }, []);

  const enqueue = useCallback((message: SnackbarMessage) => {
    const item: QueuedSnackbar = {
      ...message,
      durationMs: resolveDuration(message),
      id: message.id ?? String(nextSnackbarId++),
    };
    setQueue((items) => [...items, item]);
  }, []);

  const pause = useCallback(() => {
    setPaused(true);
  }, []);

  const resume = useCallback(() => {
    setPaused(false);
  }, []);

  useEffect(() => {
    if (timer.current !== undefined) {
      clearTimeout(timer.current);
      timer.current = undefined;
    }
    if (current === null || paused) {
      return undefined;
    }
    timer.current = setTimeout(() => {
      dismiss();
    }, current.durationMs);
    return () => {
      if (timer.current !== undefined) {
        clearTimeout(timer.current);
      }
    };
  }, [current, dismiss, paused]);

  return { current, dismiss, enqueue, pause, resume };
}
